import { AxiosResponse } from "axios";
import queryString from "query-string";
import HttpService from "src/services/httpService";
import RouteService from "src/services/routeService";
import { ApiResponse, BasicApiResponse, ListResponse, PaginatedResponse } from "src/types";

import { GroupRoles } from "@dashboard/devices/types";

import { MdmCommand } from "../types/commands";

import { unpackError } from "./helpers";

const options = {
    // data needs to be attached because of an axios bug
    // https://github.com/axios/axios/issues/86#issuecomment-139638284
    data: "",
    headers: {
        "content-type": "application/json",
    },
};

type CreateGroupPayload = {
    name: string;
    description: string;
    subscriptionId: number;
    parentId: number | null;
};
export const createGroup = async (payload: CreateGroupPayload) => {
    try {
        const endpoint = await RouteService.getGroupsRoute();
        const { data } = await HttpService.post<CreateGroupPayload, AxiosResponse<ApiResponse<API.Group>>>(endpoint, payload);
        return data.model;
    } catch (error) {
        throw unpackError(error);
    }
};

type GroupResponseOptions = {
    includeStatistics?: boolean;
    includeParents?: boolean;
};

const includeGroupDetails: Required<GroupResponseOptions> = {
    includeStatistics: true,
    includeParents: true,
};

export type UpdateGroupPayload = {
    id: number;
    name?: string;
    description?: string;
    subscriptionId?: number;
    addedLicenseIds?: number[];
} & GroupResponseOptions;

export const updateGroup = async (payload: UpdateGroupPayload) => {
    try {
        const endpoint = await RouteService.getGroupsRoute();
        const url = endpoint + String(payload.id);
        const { data } = await HttpService.patch<UpdateGroupPayload, AxiosResponse<ApiResponse<API.Group[]>>>(url, payload);
        return data.model;
    } catch (error) {
        throw unpackError(error);
    }
};

type UpdateGroupParentPayload = {
    id: number;
    parentId: number | null;
} & GroupResponseOptions;
export const updateGroupParent = async (payload: UpdateGroupParentPayload) => {
    try {
        const endpoint = await RouteService.getGroupsRoute();
        const { data } = await HttpService.patch<UpdateGroupParentPayload, AxiosResponse<ApiResponse<API.Group[]>>>(
            endpoint + String(payload.id) + "/parent",
            { ...payload, ...includeGroupDetails },
        );
        return data.model;
    } catch (error) {
        throw unpackError(error);
    }
};

type DeleteGroupPayload = {
    id: number;
};
export const deleteGroup = async (payload: DeleteGroupPayload) => {
    try {
        const endpoint = await RouteService.getGroupsRoute();
        await HttpService.delete(endpoint + String(payload.id));
        return Promise.resolve();
    } catch (error) {
        throw unpackError(error);
    }
};

type FetchGroupMdmCommandsPayload = {
    groupId: number;
    pageSize?: number;
    pageIndex?: number;
    sortCriteria?: string;
    sortDirection?: string;
};
export const fetchGroupMdmCommands = async (payload: FetchGroupMdmCommandsPayload) => {
    try {
        const endpoint = await RouteService.getGroupsRoute();
        const { data } = await HttpService.get<string, AxiosResponse<PaginatedResponse<MdmCommand>>>(
            endpoint + `${payload.groupId}/commands`,
            {
                ...options,
                params: {
                    pageSize: payload.pageSize,
                    pageIndex: payload.pageIndex || 1,
                    sortCriteria: payload.sortCriteria,
                    sortDirection: payload.sortDirection,
                },
            },
        );

        return data;
    } catch (error) {
        throw unpackError(error);
    }
};

type CreateGroupMdmCommandPayload = {
    groupId: number;
    json: Record<string, unknown>;
    commandType?: string;
    commandParameter?: string;
};
export const createGroupMdmCommand = async (payload: CreateGroupMdmCommandPayload) => {
    try {
        const endpoint = await RouteService.getGroupsRoute();
        const { data } = await HttpService.post<string, AxiosResponse<ListResponse<MdmCommand>>>(
            endpoint + `${payload.groupId}/commands`,
            { payload: payload.json },
            {
                headers: { "content-type": "application/json" },
            },
        );
        return data.list;
    } catch (error) {
        throw unpackError(error);
    }
};

interface DeleteGroupMdmCommandPayload {
    groupId: number;
    command: MdmCommand;
}
export const deleteGroupMdmCommand = async (payload: DeleteGroupMdmCommandPayload) => {
    try {
        const endpoint = await RouteService.getGroupsRoute();
        const response = await HttpService.delete<string, AxiosResponse<BasicApiResponse>>(
            endpoint + `${payload.groupId}/commands/${payload.command.id}`,
        );
        return response;
    } catch (error) {
        throw unpackError(error);
    }
};

export const fetchGroups = async (workspaceId: number) => {
    const query = queryString.stringify({ workspaceId, IncludeStatistics: true });
    const endpoint = await RouteService.getGroupsRoute();
    const fullUrl = endpoint + `?${query}`;

    const { data } = await HttpService.get<ApiResponse<API.Group[]>>(fullUrl);
    return data.model;
};

type UpdateGroupMembersPayload =
    | { id: number; addedMembers: Record<string, GroupRoles> }
    | { id: number; changedMembers: Record<string, GroupRoles> }
    | { id: number; removedMembers: string[] };
export const apiUpdateGroupMembers = async (payload: UpdateGroupMembersPayload) => {
    try {
        const endpoint = await RouteService.getGroupsRoute();
        const url = endpoint + String(payload.id);
        const { data } = await HttpService.patch<UpdateGroupMembersPayload, AxiosResponse<ApiResponse<API.Group[]>>>(
            url,
            payload,
        );

        return data.model;
    } catch (error) {
        throw unpackError(error);
    }
};
